/**
 * Video Player Component
 * Handles movie and TV show playback with iframe integration
 */

import {useCallback, useEffect, useRef, useState, useMemo} from 'react'
import {View, Platform, useWindowDimensions} from 'react-native'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useQuery} from '@tanstack/react-query'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {ArrowRotateCounterClockwise_Stroke2_Corner0_Rounded as RefreshIcon} from '#/components/icons/ArrowRotate'
import {Text} from '#/components/Typography'
import * as Select from '#/components/Select'
import {getTvSeasons, getSeasonEpisodes} from '#/lib/tmdb'
import {IS_WEB} from '#/env'

interface Server {
  serverurl: string
}

interface VideoPlayerProps {
  tmdbId: number
  contentType: 'movie' | 'tv'
  serverList: Server[]
  wt: number
}

// Default server list if none provided
const DEFAULT_SERVERS: Server[] = [
  {serverurl: 'https://vidlink.pro/movie/'},
  {serverurl: 'https://vidlink.pro/tv/'},
  {serverurl: 'https://vidsrc-embed.ru/embed/movie/'},
  {serverurl: 'https://vidsrc-embed.ru/embed/tv/'},
  {serverurl: 'https://vidsrcme.ru/embed/movie'},
  {serverurl: 'https://vidsrcme.ru/embed/tv'},
]

export function VideoPlayer({
  tmdbId,
  contentType,
  serverList = DEFAULT_SERVERS,
  wt,
}: VideoPlayerProps) {
  const t = useTheme()
  const {_} = useLingui()
  const {width: screenWidth} = useWindowDimensions()
  const [selectedSeason, setSelectedSeason] = useState<number | null>(null)
  const [selectedEpisode, setSelectedEpisode] = useState<number | null>(null)
  const [currentServerIndex, setCurrentServerIndex] = useState<number>(0)
  const requestIdRef = useRef(0)
  const iframeRef = useRef<HTMLIFrameElement | null>(null)

  // Calculate responsive dimensions
  // Original aspect ratio: 809:475 ≈ 1.703:1
  const ASPECT_RATIO = 809 / 475
  const MAX_WIDTH = 600
  // Account for navigation sidebar and padding - use a more conservative calculation
  // Layout.Center constrains to 600px on mobile, so we should respect that
  const SIDEBAR_WIDTH = 64 // Approximate sidebar width
  const CONTAINER_PADDING = 32 // Padding from parent container
  const SAFE_PADDING = 16 // Additional safety margin

  const playerDimensions = useMemo(() => {
    // Calculate available width accounting for sidebar and padding
    // On desktop, account for sidebar; on mobile, use full width minus padding
    const availableWidth = Math.max(
      300, // Minimum width
      screenWidth - SIDEBAR_WIDTH - CONTAINER_PADDING - SAFE_PADDING
    )
    const width = Math.min(MAX_WIDTH, availableWidth)
    const height = width / ASPECT_RATIO
    return {width, height}
  }, [screenWidth])

  // Get server URL based on content type - filter by /movie or /tv in URL
  const getServerUrl = useCallback((type: 'movie' | 'tv', forceRefresh = false) => {
    const servers = serverList.length > 0 ? serverList : DEFAULT_SERVERS
    // Filter servers by content type (movie or tv) based on URL
    const filteredServers = servers.filter(server => {
      const url = server.serverurl.toLowerCase()
      if (type === 'movie') {
        // Check for /movie or /embed/movie in URL
        return url.includes('/movie') || url.includes('movie')
      } else {
        // Check for /tv or /embed/tv in URL
        return url.includes('/tv') || url.includes('tv')
      }
    })
    // If no matching server found, fall back to all servers
    const availableServers = filteredServers.length > 0 ? filteredServers : servers
    
    // Use stored index if valid and not forcing refresh, otherwise pick random
    let serverIndex: number
    const storedServer = availableServers[currentServerIndex]
    if (storedServer && !forceRefresh) {
      serverIndex = currentServerIndex
    } else {
      serverIndex = Math.floor(Math.random() * availableServers.length)
      setCurrentServerIndex(serverIndex)
    }
    
    return availableServers[serverIndex].serverurl
  }, [serverList, currentServerIndex])

  // Get iframe element (web only)
  const getIframe = useCallback((): HTMLIFrameElement | null => {
    if (!IS_WEB) return null
    return iframeRef.current || (document.getElementById('shxrePlayer') as HTMLIFrameElement | null)
  }, [])

  // Fetch seasons for TV shows
  const {
    data: seasons = [],
    isLoading: seasonsLoading,
    error: seasonsError,
  } = useQuery({
    queryKey: ['tv-seasons', tmdbId],
    queryFn: async ({signal}) => {
      const currentRequestId = ++requestIdRef.current
      const results = await getTvSeasons(tmdbId, signal)
      if (currentRequestId !== requestIdRef.current) {
        return []
      }
      return results
    },
    enabled: contentType === 'tv' && !!tmdbId,
    staleTime: 5 * 60 * 1000,
  })

  // Fetch episodes when season is selected
  const {
    data: episodes = [],
    isLoading: episodesLoading,
  } = useQuery({
    queryKey: ['tv-episodes', tmdbId, selectedSeason],
    queryFn: async ({signal}) => {
      if (!selectedSeason) return []
      const currentRequestId = ++requestIdRef.current
      const results = await getSeasonEpisodes(tmdbId, selectedSeason, signal)
      if (currentRequestId !== requestIdRef.current) {
        return []
      }
      return results
    },
    enabled: contentType === 'tv' && !!tmdbId && selectedSeason !== null,
    staleTime: 5 * 60 * 1000,
  })

  // Initialize season selection
  useEffect(() => {
    if (contentType === 'tv' && seasons.length > 0 && selectedSeason === null) {
      // Find season 1 or first available season
      const season1 = seasons.find(s => s.season_number === 1)
      setSelectedSeason(season1 ? 1 : seasons[0]?.season_number || null)
    }
  }, [contentType, seasons, selectedSeason])

  // Initialize episode selection
  useEffect(() => {
    if (
      contentType === 'tv' &&
      episodes.length > 0 &&
      selectedEpisode === null
    ) {
      // Find episode 1 or first available episode
      const episode1 = episodes.find(e => e.episode_number === 1)
      setSelectedEpisode(episode1 ? 1 : episodes[0]?.episode_number || null)
    }
  }, [contentType, episodes, selectedEpisode])

  // Reset episode when season changes
  useEffect(() => {
    if (contentType === 'tv' && selectedSeason !== null) {
      setSelectedEpisode(null)
    }
  }, [contentType, selectedSeason])

  // Build player URL
  const buildSrc = useCallback(
    (startAtSeconds: number = 0, forceRefresh = false): string => {
      if (contentType === 'movie') {
        const serverUrl = getServerUrl('movie', forceRefresh)
        
        // Check if it's vidsrc-embed.ru format
        if (serverUrl.includes('vidsrc-embed.ru')) {
          const BASE_URL = `${serverUrl}${tmdbId}`
          const params = new URLSearchParams()
          params.set('autoplay', '1')
          if (startAtSeconds > 0) {
            params.set('t', startAtSeconds.toString())
          }
          return `${BASE_URL}?${params.toString()}`
        } else if (serverUrl.includes('vidsrcme.ru')) {
          // vidsrcme.ru format: query parameters
          const params = new URLSearchParams()
          params.set('tmdb', tmdbId.toString())
          params.set('autoplay', '1')
          if (startAtSeconds > 0) {
            params.set('t', startAtSeconds.toString())
          }
          return `${serverUrl}?${params.toString()}`
        } else {
          // vidlink.pro format
          const PARAMS = 'autoPlay=true'
          const BASE_URL = `${serverUrl}${tmdbId}`
          return startAtSeconds > 0
            ? `${BASE_URL}?${PARAMS}&t=${startAtSeconds}`
            : `${BASE_URL}?${PARAMS}`
        }
      } else {
        // TV show
        if (selectedSeason === null || selectedEpisode === null) {
          return 'about:blank'
        }
        const serverUrl = getServerUrl('tv', forceRefresh)
        
        // Check if it's vidsrc-embed.ru format
        if (serverUrl.includes('vidsrc-embed.ru')) {
          const BASE_URL = `${serverUrl}${tmdbId}`
          const params = new URLSearchParams()
          params.set('season', selectedSeason.toString())
          params.set('episode', selectedEpisode.toString())
          params.set('autoplay', '1')
          if (startAtSeconds > 0) {
            params.set('t', startAtSeconds.toString())
          }
          return `${BASE_URL}?${params.toString()}`
        } else if (serverUrl.includes('vidsrcme.ru')) {
          // vidsrcme.ru format: query parameters
          const params = new URLSearchParams()
          params.set('tmdb', tmdbId.toString())
          params.set('season', selectedSeason.toString())
          params.set('episode', selectedEpisode.toString())
          params.set('autoplay', '1')
          if (startAtSeconds > 0) {
            params.set('t', startAtSeconds.toString())
          }
          return `${serverUrl}?${params.toString()}`
        } else {
          // vidlink.pro format
          const PARAMS = 'autoPlay=true'
          const BASE_URL = `${serverUrl}${tmdbId}/${selectedSeason}/${selectedEpisode}`
          return startAtSeconds > 0
            ? `${BASE_URL}?${PARAMS}&t=${startAtSeconds}`
            : `${BASE_URL}?${PARAMS}`
        }
      }
    },
    [contentType, tmdbId, selectedSeason, selectedEpisode, getServerUrl],
  )

  // Load player with saved progress
  const loadPlayer = useCallback((forceRefresh = false) => {
    if (!IS_WEB) return

    const iframe = getIframe()
    if (!iframe) return

    const PLAYER_KEY = `shxre_vidsrc_${tmdbId}`
    
    // For TV shows, wait for season/episode selection
    if (contentType === 'tv' && (selectedSeason === null || selectedEpisode === null)) {
      return
    }

    try {
      // Read saved progress from localStorage (skip if forcing refresh)
      const saved = forceRefresh ? null : localStorage.getItem(PLAYER_KEY)
      let lastTime = 0

      if (saved) {
        try {
          const parsed = JSON.parse(saved)
          if (parsed?.progress?.watched) {
            lastTime = parsed.progress.watched
          }
        } catch (e) {
          // Invalid JSON, ignore
        }
      }

      const finalURL = buildSrc(lastTime, forceRefresh)
      
      // Check if this is a vidsrc server - they require explicit attribute setting
      const isVidsrcServer = finalURL.includes('vidsrc-embed.ru') || finalURL.includes('vidsrcme.ru')
      
      if (isVidsrcServer) {
        // For vidsrc servers, we need to ensure attributes are set BEFORE src
        // Remove any existing src first to reset the iframe
        iframe.src = 'about:blank'
        
        // Set all required attributes via DOM (lowercase as VidSrc expects)
        iframe.setAttribute('allowfullscreen', '')
        iframe.setAttribute('referrerpolicy', 'origin')
        iframe.setAttribute('frameborder', '0')
        
        // Legacy browser support attributes
        iframe.setAttribute('webkitallowfullscreen', 'true')
        iframe.setAttribute('mozallowfullscreen', 'true')
        iframe.setAttribute('msallowfullscreen', 'true')
        
        // Also set via properties for compatibility
        iframe.allowFullscreen = true
        iframe.referrerPolicy = 'origin'
        iframe.frameBorder = '0'
        
        // Set allow attribute with all permissions
        iframe.setAttribute('allow', 'encrypted-media; fullscreen; autoplay; picture-in-picture')
        
        // Force a small delay to ensure attributes are processed
        // Then set the src
        setTimeout(() => {
          if (iframe && finalURL && finalURL !== 'about:blank') {
            iframe.src = finalURL
          }
        }, 10)
        return // Exit early, setTimeout will handle src setting
      }
      
      if (finalURL && finalURL !== 'about:blank') {
        iframe.src = finalURL
      } else {
        // Fallback
        const fallbackURL = buildSrc(0, forceRefresh)
        if (fallbackURL && fallbackURL !== 'about:blank') {
          const isVidsrcFallback = fallbackURL.includes('vidsrc-embed.ru') || fallbackURL.includes('vidsrcme.ru')
          if (isVidsrcFallback) {
            iframe.src = 'about:blank'
            iframe.setAttribute('allowfullscreen', '')
            iframe.setAttribute('referrerpolicy', 'origin')
            iframe.setAttribute('frameborder', '0')
            iframe.setAttribute('webkitallowfullscreen', 'true')
            iframe.setAttribute('mozallowfullscreen', 'true')
            iframe.setAttribute('msallowfullscreen', 'true')
            iframe.setAttribute('allow', 'encrypted-media; fullscreen; autoplay; picture-in-picture')
            iframe.allowFullscreen = true
            iframe.referrerPolicy = 'origin'
            iframe.frameBorder = '0'
            setTimeout(() => {
              if (iframe) iframe.src = fallbackURL
            }, 10)
          } else {
            iframe.src = fallbackURL
          }
        }
      }
    } catch (e) {
      // Fallback on error
      const fallbackURL = buildSrc(0, forceRefresh)
      if (fallbackURL && fallbackURL !== 'about:blank') {
        const isVidsrcFallback = fallbackURL.includes('vidsrc-embed.ru') || fallbackURL.includes('vidsrcme.ru')
        if (isVidsrcFallback) {
          iframe.src = 'about:blank'
          iframe.setAttribute('allowfullscreen', '')
          iframe.setAttribute('referrerpolicy', 'origin')
          iframe.setAttribute('frameborder', '0')
          iframe.setAttribute('webkitallowfullscreen', 'true')
          iframe.setAttribute('mozallowfullscreen', 'true')
          iframe.setAttribute('msallowfullscreen', 'true')
          iframe.setAttribute('allow', 'encrypted-media; fullscreen; autoplay; picture-in-picture')
          iframe.allowFullscreen = true
          iframe.referrerPolicy = 'origin'
          iframe.frameBorder = '0'
          setTimeout(() => {
            if (iframe) iframe.src = fallbackURL
          }, 10)
        } else {
          iframe.src = fallbackURL
        }
      }
    }
  }, [tmdbId, contentType, selectedSeason, selectedEpisode, buildSrc, getIframe])

  // Handle server refresh: pick a new random server and reload
  const handleServerRefresh = useCallback(() => {
    const servers = serverList.length > 0 ? serverList : DEFAULT_SERVERS
    const filtered = servers.filter(server => {
      if (contentType === 'movie') {
        return (
          server.serverurl.includes('/movie/') ||
          server.serverurl.includes('/embed/movie') ||
          (server.serverurl.includes('vidsrcme.ru') &&
            server.serverurl.includes('movie'))
        )
      }
      return (
        server.serverurl.includes('/tv/') ||
        server.serverurl.includes('/embed/tv') ||
        (server.serverurl.includes('vidsrcme.ru') &&
          server.serverurl.includes('tv'))
      )
    })
    if (filtered.length > 0) {
      setCurrentServerIndex(Math.floor(Math.random() * filtered.length))
    }
  }, [serverList, contentType])

  // Ensure iframe attributes are set correctly (especially for VidSrc)
  useEffect(() => {
    if (!IS_WEB) return
    const iframe = getIframe()
    if (!iframe) return

    // Always ensure these attributes are set for fullscreen support
    iframe.setAttribute('allowfullscreen', '')
    iframe.setAttribute('referrerpolicy', 'origin')
    iframe.setAttribute('frameborder', '0')
    
    // Legacy browser support attributes
    iframe.setAttribute('webkitallowfullscreen', 'true')
    iframe.setAttribute('mozallowfullscreen', 'true')
    iframe.setAttribute('msallowfullscreen', 'true')
    
    // Set via properties for compatibility
    iframe.allowFullscreen = true
    iframe.referrerPolicy = 'origin'
    iframe.frameBorder = '0'
    
    // Ensure allow attribute is set
    iframe.setAttribute('allow', 'encrypted-media; fullscreen; autoplay; picture-in-picture')
  }, [getIframe])

  // Load player when ready
  useEffect(() => {
    if (!IS_WEB) return

    let cleanup: (() => void) | undefined

    if (contentType === 'movie') {
      // Load immediately for movies
      if (window.document.readyState === 'complete') {
        loadPlayer()
      } else {
        const loadHandler = () => loadPlayer()
        window.addEventListener('load', loadHandler)
        cleanup = () => window.removeEventListener('load', loadHandler)
      }
    } else {
      // For TV shows, load when season/episode are selected
      if (selectedSeason !== null && selectedEpisode !== null) {
        loadPlayer()
      }
    }

    return cleanup
  }, [contentType, selectedSeason, selectedEpisode, currentServerIndex, loadPlayer])

  // Handle season change
  const handleSeasonChange = useCallback((value: string) => {
    setSelectedSeason(parseInt(value, 10))
  }, [])

  // Handle episode change
  const handleEpisodeChange = useCallback((value: string) => {
    setSelectedEpisode(parseInt(value, 10))
  }, [])

  // Season options
  const seasonOptions = seasons
    .filter(s => s.season_number > 0) // Filter out special seasons
    .map(s => ({
      label: `Season ${s.season_number}${s.name ? ` — ${s.name}` : ''}`,
      value: s.season_number.toString(),
    }))

  // Episode options
  const episodeOptions = episodes.map(e => ({
    label: `E${e.episode_number}${e.name ? ` — ${e.name}` : ''}`,
    value: e.episode_number.toString(),
  }))

  if (!IS_WEB) {
    return (
      <View style={[a.align_center, a.justify_center, a.p_lg]}>
        <Text style={[a.text_lg, t.atoms.text_contrast_medium]}>
          Video player is only available on web
        </Text>
      </View>
    )
  }

  // Get available servers for display
  const availableServers = useMemo(() => {
    const servers = serverList.length > 0 ? serverList : DEFAULT_SERVERS
    return servers.filter(server => {
      if (contentType === 'movie') {
        return server.serverurl.includes('/movie/') || 
               server.serverurl.includes('/embed/movie') ||
               (server.serverurl.includes('vidsrcme.ru') && server.serverurl.includes('movie'))
      } else {
        return server.serverurl.includes('/tv/') || 
               server.serverurl.includes('/embed/tv') ||
               (server.serverurl.includes('vidsrcme.ru') && server.serverurl.includes('tv'))
      }
    })
  }, [serverList, contentType])

  // Server dropdown options: Server 1, Server 2, etc.
  const serverOptions = useMemo(
    () =>
      availableServers.map((_, i) => ({
        label: `Server ${i + 1}`,
        value: i.toString(),
      })),
    [availableServers],
  )

  const handleServerChange = useCallback((value: string) => {
    const index = parseInt(value, 10)
    if (!Number.isNaN(index) && index >= 0 && index < availableServers.length) {
      setCurrentServerIndex(index)
    }
  }, [availableServers.length])

  return (
    <View
      style={[
        a.gap_md,
        {
          maxWidth: MAX_WIDTH,
          alignSelf: 'center',
          width: '100%',
        },
      ]}>
      {/* Server dropdown + Refresh button */}
      <View style={[a.flex_row, a.align_center, a.gap_md, a.justify_between]}>
        <Text style={[a.text_sm, a.font_medium, t.atoms.text_contrast_medium]}>
          Server:
        </Text>
        <View style={[a.flex_row, a.align_center, a.gap_sm]}>
          <View style={[{minWidth: 120}]}>
            <Select.Root
              value={
                availableServers.length > 0
                  ? Math.min(
                      currentServerIndex,
                      availableServers.length - 1,
                    ).toString()
                  : '0'
              }
              onValueChange={handleServerChange}>
              <Select.Trigger label="Server">
                <Select.ValueText>
                  {() =>
                    availableServers.length > 0
                      ? `Server ${Math.min(currentServerIndex, availableServers.length - 1) + 1}`
                      : 'Server 1'
                  }
                </Select.ValueText>
                <Select.Icon />
              </Select.Trigger>
              <Select.Content
                label="Server Options"
                renderItem={({label, value}) => (
                  <Select.Item value={value} label={label}>
                    <Select.ItemText>{label}</Select.ItemText>
                    <Select.ItemIndicator />
                  </Select.Item>
                )}
                items={serverOptions}
              />
            </Select.Root>
          </View>
          <Button
            label={_(msg`Refresh server`)}
            onPress={handleServerRefresh}
            color="secondary"
            size="small"
            shape="round">
            <ButtonIcon icon={RefreshIcon} />
          </Button>
        </View>
      </View>

      {/* Season/Episode Dropdowns (TV shows only) */}
      {contentType === 'tv' && (
        <View
          style={[
            a.flex_row,
            a.gap_md,
            a.flex_wrap,
            {
              maxWidth: MAX_WIDTH,
              alignSelf: 'center',
              width: '100%',
            },
          ]}>
          <View style={[a.flex_1, {minWidth: Math.min(200, playerDimensions.width - 32)}]}>
            <Select.Root
              value={selectedSeason?.toString()}
              onValueChange={handleSeasonChange}
              disabled={seasonsLoading}>
              <Select.Trigger label="Season">
                <Select.ValueText
                  placeholder="Select Season">
                  {value => {
                    if (seasonsLoading) return 'Loading...'
                    if (selectedSeason !== null) return `Season ${selectedSeason}`
                    return 'Select Season'
                  }}
                </Select.ValueText>
                <Select.Icon />
              </Select.Trigger>
              <Select.Content
                items={seasonOptions}
                renderItem={(item, index, selectedValue) => (
                  <Select.Item
                    key={item.value}
                    label={item.label}
                    value={item.value}>
                    <Select.ItemText>{item.label}</Select.ItemText>
                    <Select.ItemIndicator />
                  </Select.Item>
                )}
              />
            </Select.Root>
          </View>

          <View style={[a.flex_1, {minWidth: Math.min(200, playerDimensions.width - 32)}]}>
            <Select.Root
              value={selectedEpisode?.toString()}
              onValueChange={handleEpisodeChange}
              disabled={episodesLoading || selectedSeason === null}>
              <Select.Trigger label="Episode">
                <Select.ValueText
                  placeholder="Select Episode">
                  {value => {
                    if (episodesLoading) return 'Loading...'
                    if (selectedEpisode !== null) return `Episode ${selectedEpisode}`
                    return 'Select Episode'
                  }}
                </Select.ValueText>
                <Select.Icon />
              </Select.Trigger>
              <Select.Content
                items={episodeOptions}
                renderItem={(item, index, selectedValue) => (
                  <Select.Item
                    key={item.value}
                    label={item.label}
                    value={item.value}>
                    <Select.ItemText>{item.label}</Select.ItemText>
                    <Select.ItemIndicator />
                  </Select.Item>
                )}
              />
            </Select.Root>
          </View>
        </View>
      )}

      {/* Error states */}
      {contentType === 'tv' && seasonsError && (
        <View style={[a.p_md, a.rounded_md, {backgroundColor: t.palette.negative_50}]}>
          <Text style={[a.text_sm, {color: t.palette.negative_900}]}>
            Failed to load seasons. Please try again.
          </Text>
        </View>
      )}

      {/* Player Container */}
      <View
        style={[
          {
            width: playerDimensions.width,
            height: playerDimensions.height,
            borderRadius: 12,
            overflow: 'hidden',
            backgroundColor: '#000', // Keep black for video player (standard)
            alignSelf: 'center',
            maxWidth: '100%',
          },
          Platform.OS === 'web' && {
            boxShadow: '0 6px 30px rgba(99,184,188,0.18)',
          },
        ]}>
        {IS_WEB ? (
          <iframe
            ref={iframeRef}
            id="shxrePlayer"
            style={{
              width: '100%',
              height: '100%',
              border: 0,
              display: 'block',
            }}
            src="about:blank"
            frameBorder="0"
            referrerPolicy="origin"
            allowFullScreen={true}
            allow="encrypted-media; fullscreen; autoplay; picture-in-picture"
          />
        ) : (
          <View
            style={[
              a.flex_1,
              a.align_center,
              a.justify_center,
              {backgroundColor: '#000'}, // Keep black for video player
            ]}>
            <Text style={[t.atoms.text]}>Video player not available</Text>
          </View>
        )}
      </View>
    </View>
  )
}
